// variant of src/components/shared/production-listing/component.tsx

import React, { useContext } from 'react'

import { withOptimizely, WithOptimizelyProps } from '@optimizely/react-sdk'
import { ArrowForwardIcon, Box, Column, Grid, ProductionListingRow } from '@vividseats/vivid-ui-kit'
import ProductionListingRowButton from '@vividseats/vivid-ui-kit/dist/components/production-listing-row/production-listing-row-button'
import NextLink from 'next/link'

import { useShouldOpenProductionInNewTab } from '@/components/shared/production-listings/hooks/should-open-in-new-tab/hook'
import ProductionListContext from '@/context/productions-list'
import { useAnalytics } from '@/hooks/use-analytics'
import { OPTIMIZELY_EVENT } from '@/optimizely/constants'
import type { ProductionListSelectorWithRowsInfoType } from '@/types/app-types'
import { trackSelectProduction } from '@/utils/analytics'
import { trackEvent } from '@/utils/analytics/ga360'

import styles from './styles.module.scss'

const VenueProductionListing: React.FC<ProductionListSelectorWithRowsInfoType & WithOptimizelyProps> = ({
    id,
    isExternal,
    date,
    day,
    time,
    href,
    label,
    title,
    optimizely,
    analytics,
}) => {
    const {
        selectors: { analyticsSubIds },
    } = useContext(ProductionListContext)

    const { analyticsId, analyticsPageType } = useAnalytics(...analyticsSubIds)

    const shouldOpenInNewTab = useShouldOpenProductionInNewTab()

    const additionalProductionListingRowProps = {
        target: shouldOpenInNewTab ? '_blank' : undefined,
    }

    const handleClick = () => {
        trackEvent('Production Selection', `${analyticsPageType} Page`, 'All Dates', id, false)
        optimizely?.track(OPTIMIZELY_EVENT.productionLinkClick)

        trackSelectProduction({
            item_list_name: analyticsId,
            index: analytics?.index,
            production: analytics?.production,
        })
    }

    const productionListingRow: JSX.Element = (
        <Column p={0.5}>
            <NextLink href={href} passHref>
                <ProductionListingRow
                    data-testid={`production-listing-row-${id}`}
                    date={date}
                    day={day}
                    time={time}
                    href={href}
                    label={label.replace(/-([^-]*)$/, ' • $1')}
                    title={title}
                    mobileDivider={false}
                    onClickCapture={handleClick}
                    altFont
                    titleColumnProps={{ ml: 1 }}
                    classes={styles}
                    {...additionalProductionListingRowProps}
                >
                    <ProductionListingRowButton />
                    <Box
                        alignSelf="center"
                        marginLeft={5}
                        mobileDisplay="block"
                        tabletDisplay="none"
                        desktopDisplay="none"
                    >
                        <ArrowForwardIcon />
                    </Box>
                </ProductionListingRow>
            </NextLink>
        </Column>
    )

    return (
        <Grid key={id}>
            {isExternal ? (
                productionListingRow
            ) : (
                <NextLink passHref href={href}>
                    {productionListingRow}
                </NextLink>
            )}
        </Grid>
    )
}

export default withOptimizely(VenueProductionListing)
